import * as _ from 'underscore';
import { CatalogContentType, CatalogContentTypes } from '../../catalog/CatalogContentType';
import { ExplorableItemType, SpaceInfoItemType } from '../../ts/models/ContentType';
import { ISpaceTypeInfo } from '../../ts/models/resourceInfoParts';
import { SiteSpaceContentType as SiteSubspaceName } from './SiteSpaceContentTypeState';


// DON'T ADD IMPORTS FROM CONTENTDEFINITION HERE. IT CAUSES BUILD ERRORS FOR SOME REASON.
// This is probably because these interfaces are being depended upon from the integration tests,
// which use a stricter tsconfig than the main source code does.
export type DatasourceSubspaceName = 'nlp' | 'connections' | 'extractRefreshes' | 'remoteRefreshSchedules' | 'connectedWorkbooks' | 'lineage' | 'askDataUsageAnalytics';
export type FlowSubspaceName = 'overview' | 'connections' | 'scheduledTasks' | 'runHistory' | 'lineage';
export type PublishedConnectionSubspaceName = 'overview';
export type WorkbookSubspaceName = 'views' | 'datasources' | 'connectedMetrics' | 'subscriptions' | 'dataconnections' | 'extractTasks' | 'lineage';
export type SearchSubspaceName = 'views' | 'metrics' | 'workbooks' | 'datasources' | 'projects' | 'flows' | 'tables' | 'databases' | 'dataRoles' | 'users' | 'publishedConnections';
export type DataRoleSubspaceName = 'definition' | 'connectedFlows' | 'connectedDatasources';
export type MetricsSubspaceName = 'favorites' | 'all' | 'owned';
export type GroupSubspaceName = 'users' | 'details';
export type SiteUserSubspaceName = 'content' | 'alerts' | 'subscriptions' | 'settings';
export type LegacySiteUserSubspaceName = 'projects' | 'workbooks' | 'views' | 'metrics' | 'datasources' | 'dataconnections' |  'flows' | 'publishedConnections';
export type SiteTasksSubspaceName = 'extractRefreshes' | 'runFlowTasks' | 'subscriptions' | 'alerts';
export type ServerTasksSubspaceName = 'extractRefreshes' | 'runFlowTasks' | 'subscriptions';
export type ScheduleSubspaceName = 'extractRefreshes' | 'subscriptions' | 'flows' | 'details';
export type SiteSettingsSubspaceName = 'settings' | 'extensions' | 'bridge' | 'authentication';
export type ServerSettingsSubspaceName = 'settings' | 'extensions' | 'addsite' | 'licenses' | 'customization';
export type SubspaceName =
  WorkbookSubspaceName |
  DatasourceSubspaceName |
  FlowSubspaceName |
  SiteSubspaceName |
  DataRoleSubspaceName |
  SearchSubspaceName |
  MetricsSubspaceName |
  PublishedConnectionSubspaceName |
  GroupSubspaceName |
  SiteUserSubspaceName |
  SiteTasksSubspaceName |
  ScheduleSubspaceName |
  SiteSettingsSubspaceName |
  ServerSettingsSubspaceName |
  ServerTasksSubspaceName;
export const TopLevelProjectId: string = null;

// all content types need to have a space
export type SpaceType = SpaceInfoItemType | 'site' | 'sites' | 'sitesettings'  | 'serversettings' |'schedules' | 'favorites' | 'recents' |
    'home' | 'search' | 'unifiedSearch' | 'backgroundJobs' | 'siteuser' | 'siteusers' | 'serveruser' | 'serverusers' |'recommendations' | 'metric' | 'sitestatus' |
    'sitetasks' | 'siteanalysis' | 'serveranalysis' | 'serverstatus' | 'servertasks' | 'catalog' | 'groups' | 'revisionhistory' | 'servertasks' | 'collections' | 'starters';
export interface SpaceInfo {
  item: ISpaceTypeInfo;
  spaceType: SpaceType;
  subspaceName: SubspaceName;
}

export const subspaceNameToCatalogContentType = (subspaceName: SubspaceName): CatalogContentType => {
  if (CatalogContentTypes.hasOwnProperty(subspaceName)) {
    const matchingTypes = Object.entries(CatalogContentTypes)
      .filter(([key, value]) => key === subspaceName)
      .map(([key, value]) => value);

    if (matchingTypes.length > 0) {
      return matchingTypes[0];
    }
  }

  if (subspaceName === 'databases') {
    return 'database';
  } else if (subspaceName === 'tables') {
    return 'table';
  }

  throw new Error('No catalog content type mapping for provided subspace name');
};

// TODO: Add subspace configuration object (TFS: 827255)
// Maps a Workbook sub-space to a ExplorableItemType.
export const workbookSubspaceNameToExplorableItemType = (workbookSubSpaceName: WorkbookSubspaceName): ExplorableItemType | '' => {
  if (workbookSubSpaceName === 'views') {
    return 'view';
  } else if (workbookSubSpaceName === 'datasources') {
    return 'datasource';
  } else if (workbookSubSpaceName === 'subscriptions') {
    return 'subscription';
  } else if (workbookSubSpaceName === 'dataconnections') {
    return 'dataconnection';
  } else if (workbookSubSpaceName === 'extractTasks') {
    return 'extractTask';
  } else if (workbookSubSpaceName === 'connectedMetrics') {
    return 'metric';
  }

  return '';
};

// TODO: Add subspace configuration object (TFS: 827255)
// Maps a Datasource sub-space to a ExplorableItemType.
export const datasourceSubspaceNameToExplorableItemType = (datasourceSubSpaceName: DatasourceSubspaceName): ExplorableItemType | '' => {
  if (datasourceSubSpaceName === 'connections') {
    return 'connection';
  } else if (datasourceSubSpaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (datasourceSubSpaceName === 'remoteRefreshSchedules') {
    return 'remoteRefreshSchedule';
  } else if (datasourceSubSpaceName === 'connectedWorkbooks') {
    return 'connectedWorkbook';
  }

  return '';
};

export const dataRoleSubspaceNameToExplorableItemType = (dataRoleSubSpaceName: DataRoleSubspaceName): ExplorableItemType | '' => {
  if (dataRoleSubSpaceName === 'connectedFlows') {
    return 'flow';
  }
  if (dataRoleSubSpaceName === 'connectedDatasources') {
    return 'datasource';
  }

  return '';
};

export const contentTypeToWorkbookSubspaceName = (contentType: ExplorableItemType): WorkbookSubspaceName | '' => {
  if (contentType === 'view') {
    return 'views';
  } else if (contentType === 'datasource') {
    return 'datasources';
  } else if (contentType === 'dataconnection') {
    return 'dataconnections';
  }

  return '';
};

export const searchSubspaceNameToExplorableItemType = (searchSubspaceName: SearchSubspaceName): ExplorableItemType | '' => {
  if (searchSubspaceName === 'views') {
    return 'view';
  } else if (searchSubspaceName === 'metrics') {
    return 'metric';
  } else if (searchSubspaceName === 'workbooks') {
    return 'workbook';
  } else if (searchSubspaceName === 'datasources') {
    return 'datasource';
  } else if (searchSubspaceName === 'flows') {
    return 'flow';
  } else if (searchSubspaceName === 'projects') {
    return 'project';
  } else if (searchSubspaceName === 'tables') {
    return 'table';
  } else if (searchSubspaceName === 'databases') {
    return 'database';
  } else if (searchSubspaceName === 'dataRoles') {
    return 'dataRole';
  } else if (searchSubspaceName === 'users') {
    return 'user';
  } else if (searchSubspaceName === 'publishedConnections') {
    return 'publishedConnection';
  }
  return '';
};

export const scheduleSubspaceNameToExplorableItemType = (scheduleSubspaceName: ScheduleSubspaceName): ExplorableItemType | '' => {
  if (scheduleSubspaceName === 'subscriptions') {
    return 'subscription';
  } else if (scheduleSubspaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (scheduleSubspaceName === 'flows') {
    return 'runFlowTask';
  }
  return '';
};

export const groupSubspaceNameToExplorableItemType = (groupSubspaceName: GroupSubspaceName): ExplorableItemType | '' => {
  if (groupSubspaceName === 'users') {
    return 'siteuser';
  }
  return '';
};

export const siteTasksSubspaceNameToExplorableItemType = (tasksSubspaceName: SiteTasksSubspaceName): ExplorableItemType | '' => {
  if (tasksSubspaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (tasksSubspaceName === 'runFlowTasks') {
    return 'runFlowTask';
  } else if (tasksSubspaceName === 'subscriptions') {
    return 'subscription';
  } else if (tasksSubspaceName === 'alerts') {
    return 'dataalert';
  }

  return '';
};

export const serverTasksSubspaceNameToExplorableItemType = (tasksSubspaceName: ServerTasksSubspaceName): ExplorableItemType | '' => {
  if (tasksSubspaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (tasksSubspaceName === 'runFlowTasks') {
    return 'runFlowTask';
  } else if (tasksSubspaceName === 'subscriptions') {
    return 'subscription';
  }

  return '';
};

const siteUserSubspaceNameToExplorableItemTypeMap: Record<SiteUserSubspaceName, ExplorableItemType | ''> = {
  content: '',
  alerts: 'dataalert',
  subscriptions: 'subscription',
  settings: '',
};

export const siteUserSubspaceNameToExplorableItemType = (siteUserSubspaceName: SiteUserSubspaceName): ExplorableItemType | '' => {
  if (_.has(siteUserSubspaceNameToExplorableItemTypeMap, siteUserSubspaceName)) {
    return siteUserSubspaceNameToExplorableItemTypeMap[siteUserSubspaceName];
  }
  return '';
};

export const legacySiteUserSubspaceNameToExplorableItemType = (siteUserSubspaceName: LegacySiteUserSubspaceName): ExplorableItemType | '' => {
  if (siteUserSubspaceName === 'views') {
    return 'view';
  } else if (siteUserSubspaceName === 'metrics') {
    return 'metric';
  } else if (siteUserSubspaceName === 'workbooks') {
    return 'workbook';
  } else if (siteUserSubspaceName === 'datasources') {
    return 'datasource';
  } else if (siteUserSubspaceName === 'flows') {
    return 'flow';
  } else if (siteUserSubspaceName === 'projects') {
    return 'project';
  } else if (siteUserSubspaceName === 'publishedConnections') {
    return 'publishedConnection';
  }
  return '';
};

export const isSpace = (spaceInfo: SpaceInfo, spaceType: SpaceType): boolean => {
  return spaceInfo && spaceInfo.spaceType === spaceType;
};

export const IsWorkbookDatasourceSubspaceTab = (spaceInfo: SpaceInfo): boolean => {
  return spaceInfo && isWorkbookSpace(spaceInfo) && (spaceInfo.subspaceName === 'datasources' || spaceInfo.subspaceName === 'dataconnections');
};

export const IsWorkbookSubscriptionsSubspaceTab = (spaceInfo: SpaceInfo): boolean => {
  return spaceInfo && isWorkbookSpace(spaceInfo) && spaceInfo.subspaceName === 'subscriptions';
};

export function isProjectSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'project';
}

export function isMixedContentSpace(spaceInfo: SpaceInfo) {
  return isProjectSpace(spaceInfo) || isFavoritesSpace(spaceInfo) || isSiteUserContentSubspace(spaceInfo) || isCollectionSpace(spaceInfo);
}


export function isDatasourceSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'datasource';
}

export function isDatasourceConnectionSubSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'datasource' && spaceInfo.subspaceName === 'connections';
}

export function isDatasourceOwnerPanelSubSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'datasource' && spaceInfo.subspaceName === 'askDataUsageAnalytics';
}

export function isFlowSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'flow';
}

export function isPublishedConnectionSpace(spaceInfo: SpaceInfo): boolean {
  return spaceInfo.spaceType === 'publishedConnection';
}

export function isDataRoleSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'dataRole';
}

export function isWorkbookSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'workbook';
}

export function isSiteSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'site';
}

export function isSiteDatasourceSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && isSiteSpace(spaceInfo) && (spaceInfo.subspaceName === 'datasource' || spaceInfo.subspaceName === 'dataconnection');
}

export function isSiteDataRoleSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && isSiteSpace(spaceInfo) && (spaceInfo.subspaceName === 'dataRole');
}

export function isFavoritesSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'favorites';
}

export function isRecentsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'recents';
}

export function isRecommendationsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'recommendations';
}

export function isHomeSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'home';
}

export function isSomeKindaSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && (spaceInfo.spaceType === 'search' || spaceInfo.spaceType ==='unifiedSearch');
}

export function isSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'search';
}

export function isUnifiedSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'unifiedSearch';
}

export function isMetricSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'metric';
}

export function isBackgroundJobsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'backgroundJobs';
}

export function isServerUserSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'serveruser';
}

export function isAllServerUsersSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'serverusers';
}

export function isSiteUsersSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteusers';
}

export function isSiteUserSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteuser';
}

export function isSiteUserContentSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteuser' && spaceInfo.subspaceName === 'content';
}

export function isSiteUserDataAlertsSubSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteuser' && spaceInfo.subspaceName === 'alerts';
}

export function isGroupSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'group';
}

export function isAllSitesSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'sites';
}

export function isSiteTasksSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'sitetasks';
}

export function isServerTasksSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'servertasks';
}

export function isAllSchedulesSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'schedules';
}

export function isAllGroupsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'groups';
}

export function isAllCollectionsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'collections';
}

export function isCollectionSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'collection';
}

export function isScheduleSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'schedule';
}

export function isVizSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'view');
}

export function isMetricDetailSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'metric') && spaceInfo.subspaceName === undefined;
}

export function isAdminVizSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'siteanalysis') || isSpace(spaceInfo, 'serveranalysis');
}

export function isDatasourceOwnerPanelSubspace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'datasource') && spaceInfo.subspaceName === 'askDataUsageAnalytics';
}

export function isCatalogSpace(spaceInfo: SpaceInfo): boolean {
  try {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);

    return ['site', 'search'].includes(spaceInfo.spaceType) && catalogContentType != null;
  } catch (e) {
    return false;
  }
}

export function isAllStartersSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'starters';
}

// Used for spaces that are not on a specific item:
// eg: site/favorites/recents/home/catalog/search/jobs/sitestatus/sitetasks
export const SpaceItemForNonEntitySpace: ISpaceTypeInfo = {
  id: TopLevelProjectId,
  contentType: undefined  // this is not a content type!
};
